/**
 * The way into the feedback form from anywhere.
 *
 * **Global on purpose.** Until now the form hung off two footer links (`/stories`,
 * `/settings`) and one error path, which meant the screen a player was on decided whether
 * they could report a bug — and `/` redirects a returning player straight into
 * `/play/{id}`, the one screen that had no link at all. A bug can happen on any screen, so
 * the control has to exist on any screen. It is mounted once by `App`, beside the dialog and
 * outside the error boundary, so it survives the render crash it is most likely reporting.
 *
 * **Signed-in only.** `POST /feedback` takes a Google ID token, so an anonymous reader on
 * `/login` or on the public leaderboard would get a 401 for their trouble. Hiding the control
 * is the honest version of that; offering one that always fails is not.
 *
 * Two placements, one element — the pattern `CharacterPanel` and the story nav already use:
 *
 * - **`≥sm`**: the word, bottom-right. That corner is the one piece of chrome-free screen on
 *   every route — the story screen puts the character chip top-left and its nav cluster
 *   top-right, and the `Page` routes are a centred `--measure` column with empty gutters
 *   either side. `text-fg-faint` at rest so it reads as furniture, accent on hover.
 * - **`<sm`**: a `?` glyph instead, because a phone has no free corner at all: the nav bar is
 *   fixed across the top and the character panel and action bar are sticky across the foot.
 *   It sits *above* that foot with the prose scrolling under a translucent square — a real if
 *   small imposition on the reading surface, and the trade accepted for the control being
 *   reachable mid-story, which is where bugs get noticed.
 *
 * The vertical anchor is `.feedback-anchor` in `styles/index.css` rather than a Tailwind
 * `bottom-*` pair, because the mobile value is *measured*: the sticky foot's height moves with
 * the DM's choice chips, so any constant clearance is wrong on some turn in some language, and
 * wrong here means sitting on top of the action bar.
 *
 * `z-20` matches the character chip and stays under `Dialog`'s own `z-30`/`z-40`, so the
 * button cannot float over the form it just opened. That was only true on paper until `Dialog`
 * started portalling to the body: an open drawer was written inside a stacking context that
 * this button sat outside of, so it floated crisp over the character sheet and punched a hole
 * in the world panel. Now it is a plain `z-20` against the same root stacking context the
 * backdrop is, and it dims with the rest of the page.
 */
import { useAuth } from '@/auth/context';
import { openFeedback } from '@/features/feedback/store';
import { useT } from '@/i18n/context';

export function FeedbackButton() {
  const t = useT();
  const { status } = useAuth();

  if (status !== 'signed_in') return null;

  const label = t('ui.nav.feedback');

  return (
    <button
      type="button"
      // Labelled on the element rather than by the text, because below `sm` the text is a
      // glyph: `?` is not a name. The visible word above `sm` matches this string exactly.
      aria-label={label}
      title={label}
      onClick={() => {
        // No arguments: `open()` fills in the route itself, and the category defaults to
        // `feedback` — someone who reaches for this deliberately usually has an opinion, and
        // the picker in the form is one click away for the times they have a bug instead.
        openFeedback();
      }}
      className="feedback-anchor border-border bg-bg/90 text-fg-faint hover:border-accent hover:text-accent fixed right-3 z-20 flex h-9 w-9 items-center justify-center border font-mono backdrop-blur transition-colors sm:right-4 sm:h-auto sm:w-auto sm:px-3 sm:py-1.5"
      style={{ fontSize: 'var(--text-meta)' }}
    >
      <span aria-hidden className="sm:hidden">
        ?
      </span>
      <span aria-hidden className="hidden sm:inline">
        {label}
      </span>
    </button>
  );
}
